import { flatten } from 'lodash';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { concat, EMPTY, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { getType } from 'typesafe-actions';

import { ApiMethodExecutingError } from '../../service/Errors';
import { EpicDeps } from '../../service/StoreFactory';
import { State } from '../../typings/state';
import { getSelectedBatches } from '../BatchesList/helpers';
import { hideBusy, showBusy } from '../CanvasApp/actions';
import { CanvasAction as Action } from '../common/actions';
import { findItem } from '../common/helpers';

import {
    deleteCreative,
    DeleteCreative,
    deleteCreativeSuccess,
    LoadItems,
    loadItems,
    loadItemsSuccess,
    saveName,
    SaveName,
    saveNameSuccess,
    showError,
    submit,
    SubmitAction
} from './actions';
import { createCreativesCollection } from './mappers';

const loadItemsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(loadItems)),
        switchMap((action: LoadItems) =>
            from(
                deps.canvasApi.commonCreatives.getCreatives(
                    action.offset,
                    action.order,
                    store$.value.canvasEditor.sharedData
                )
            ).pipe(
                map(createCreativesCollection),
                map(collection => loadItemsSuccess(collection, action.sizes)),
                catchError((error: ApiMethodExecutingError) => of(showError(error.getNativeError().message)))
            )
        )
    );
};

const saveNameEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(saveName)),
        switchMap((action: SaveName) => {
            const batch = findItem(
                action.id,
                store$.value.canvasEditor.canvas.commonCreatives.batchesList.collection.batches
            );
            const oldName = batch.name;
            const newName = action.val ? action.val.trim() : '';
            const id = action.id;

            if (!newName || newName === oldName) {
                return of(saveNameSuccess(id, oldName));
            }

            return from(deps.canvasApi.commonCreatives.update(id, newName, store$.value.canvasEditor.sharedData)).pipe(
                map(() => saveNameSuccess(id, newName)),
                catchError((error: ApiMethodExecutingError) => of(showError(error.getNativeError().message)))
            );
        })
    );
};

const deleteEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(deleteCreative)),
        switchMap((action: DeleteCreative) => {
            const { idToDelete } = store$.value.canvasEditor.canvas.commonCreatives.batchesList;
            return from(deps.canvasApi.commonCreatives.delete(idToDelete, store$.value.canvasEditor.sharedData)).pipe(
                map(() => deleteCreativeSuccess(idToDelete)),
                catchError((error: ApiMethodExecutingError) => of(showError(error.getNativeError().message)))
            );
        })
    );
};

const submitEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(submit)),
        switchMap((action: SubmitAction) => {
            const selectedBatches = getSelectedBatches(
                store$.value.canvasEditor.canvas.commonCreatives.batchesList.collection
            );

            return concat(
                of(showBusy()),
                from(deps.canvasApi.commonCreatives.submit(selectedBatches, store$.value.canvasEditor.sharedData)).pipe(
                    switchMap(result => {
                        action.creativesSelect(flatten(selectedBatches.map(batch => batch.creativeIds)));

                        return EMPTY;
                    }),
                    catchError(error => of(showError(error.message || error.getNativeError().message), hideBusy()))
                )
            );
        })
    );
};

export const commonCreativesEpics = combineEpics(loadItemsEpic, saveNameEpic, deleteEpic, submitEpic);
